import { preCache, snapdom } from '@zumer/snapdom';
import { type RefObject, useCallback, useEffect, useRef, useState } from 'react';
import { MAX_PRELOAD_ATTEMPTS } from '../consts.ts';
import preloadBackgroundImages from '../components/utils/preloadBackgroundImages.ts';
import preloadImagesInElement from '../components/utils/preloadImagesInElement.ts';
import waitForNonZeroSize from '../components/utils/waitForNonZeroSize.ts';

export type UseCertificateDownloadProps = {
  captureOptions?: Parameters<typeof snapdom>[1];
  captureRef: RefObject<HTMLDivElement | null>;
  filename: string;
  readinessKey: unknown;
  shouldCenterImages?: boolean;
};

export type UseCertificateDownloadReturn = {
  downloadCertificate: () => Promise<void>;
  isDownloading: boolean;
  isReady: boolean;
};

const useCertificateDownload = ({
  captureOptions,
  captureRef,
  filename,
  readinessKey,
  shouldCenterImages = true,
}: UseCertificateDownloadProps): UseCertificateDownloadReturn => {
  const [isDownloading, setIsDownloading] = useState(false);
  const [readyKey, setReadyKey] = useState<unknown>(null);
  const isDownloadingRef = useRef(false);
  const isReady = Object.is(readyKey, readinessKey);

  useEffect(() => {
    const element = captureRef.current;
    if (!element) return;

    let isCancelled = false;

    const prepareCertificate = async (): Promise<void> => {
      for (let attempt = 1; attempt <= MAX_PRELOAD_ATTEMPTS; attempt += 1) {
        try {
          await preloadImagesInElement(element, { shouldCenterImages });
          const backgroundResults = await preloadBackgroundImages(element);
          const backgroundsLoaded = backgroundResults.every(
            (result) => result.status === 'fulfilled'
          );

          if (!backgroundsLoaded) continue;

          await preCache(element);
          if (!isCancelled) setReadyKey(readinessKey);
          return;
        } catch (error) {
          if (attempt === MAX_PRELOAD_ATTEMPTS) {
            console.error('Failed to prepare finisher certificate assets.', error);
          }
        }
      }
    };

    prepareCertificate().catch((error: unknown) => {
      console.error(error);
    });

    return () => {
      isCancelled = true;
    };
  }, [captureRef, readinessKey, shouldCenterImages]);

  const downloadCertificate = useCallback(async (): Promise<void> => {
    const element = captureRef.current;
    if (!element || !isReady || isDownloadingRef.current) return;

    isDownloadingRef.current = true;
    setIsDownloading(true);
    // Preloading inlines asset data URLs, so capture a detached copy to keep those
    // mutations away from the rendered certificate.
    const captureTarget = element.cloneNode(true) as HTMLElement;
    document.body.appendChild(captureTarget);

    try {
      await waitForNonZeroSize(captureTarget);
      await preloadImagesInElement(captureTarget, { shouldCenterImages });
      await preloadBackgroundImages(captureTarget);
      await preCache(captureTarget);

      const capture = await snapdom(captureTarget, captureOptions);
      // Double generate the PNG to ensure the image is fully rendered.
      await capture.toPng().then(async () => {
        await capture.download({ type: 'png', filename });
      });
    } catch (error) {
      console.error(error);
    } finally {
      captureTarget.remove();
      isDownloadingRef.current = false;
      setIsDownloading(false);
    }
  }, [captureOptions, captureRef, filename, isReady, shouldCenterImages]);

  return { downloadCertificate, isDownloading, isReady };
};

export default useCertificateDownload;
